import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('nurture_token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('nurture_refresh_token'));
  const [loading, setLoading] = useState(true);
  const [family, setFamily] = useState(null);

  // E2.6-F: Handle Google OAuth callback
  const processGoogleCallback = useCallback(async () => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];
      if (sessionId) {
        try {
          const response = await axios.post(`${API}/auth/google/session`, null, {
            params: { session_id: sessionId }
          });
          const { token: newToken, refresh_token, user: userData } = response.data;
          
          localStorage.setItem('nurture_token', newToken);
          if (refresh_token) {
            localStorage.setItem('nurture_refresh_token', refresh_token);
            setRefreshToken(refresh_token);
          }
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          setToken(newToken);
          setUser(userData);
          
          // Clear the hash from URL
          window.history.replaceState(null, '', window.location.pathname);
          
          return true;
        } catch (error) {
          console.error('Google OAuth error:', error);
          window.history.replaceState(null, '', '/login');
        }
      }
    }
    return false;
  }, []);

  useEffect(() => {
    const init = async () => {
      // E2.6-F: Check for Google OAuth callback first
      // CRITICAL: If returning from OAuth callback, process session_id first
      if (window.location.hash?.includes('session_id=')) {
        const processed = await processGoogleCallback();
        if (processed) {
          setLoading(false);
          return;
        }
      }
      
      // Normal token-based auth
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchUser();
      } else {
        setLoading(false);
      }
    };
    init();
  }, [token, processGoogleCallback]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      if (response.data.family_id) {
        fetchFamily();
      }
    } catch (error) {
      console.error('Auth error:', error);
      // E2.6-F: Try refresh token before logout
      if (error.response?.status === 401 && refreshToken) {
        try {
          const refreshResponse = await axios.post(`${API}/auth/refresh`, null, {
            params: { refresh_token: refreshToken }
          });
          const { token: newToken, refresh_token: newRefresh } = refreshResponse.data;
          localStorage.setItem('nurture_token', newToken);
          localStorage.setItem('nurture_refresh_token', newRefresh);
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          setToken(newToken);
          setRefreshToken(newRefresh);
          // Retry fetchUser
          const retryResponse = await axios.get(`${API}/auth/me`);
          setUser(retryResponse.data);
          if (retryResponse.data.family_id) {
            fetchFamily();
          }
          return;
        } catch (refreshError) {
          console.error('Refresh token failed:', refreshError);
        }
      }
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchFamily = async () => {
    try {
      const response = await axios.get(`${API}/family`);
      setFamily(response.data);
    } catch (error) {
      console.error('Family fetch error:', error);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, refresh_token, user: userData } = response.data;
    localStorage.setItem('nurture_token', newToken);
    if (refresh_token) {
      localStorage.setItem('nurture_refresh_token', refresh_token);
      setRefreshToken(refresh_token);
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    if (userData.family_id) {
      fetchFamily();
    }
    return userData;
  };

  const register = async (email, password, name, familyName) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      family_name: familyName
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('nurture_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    if (userData.family_id) {
      fetchFamily();
    }
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('nurture_token');
    localStorage.removeItem('nurture_refresh_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setFamily(null);
  };

  const addFamilyMember = async (member) => {
    const response = await axios.post(`${API}/family/member`, member);
    await fetchFamily();
    return response.data;
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      family,
      loading,
      login,
      register,
      logout,
      addFamilyMember,
      fetchFamily,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
